/**
 * TanStack Query hook for GET /api/v1/admin/security/login-attempts.
 *
 * The query key includes the filter snapshot + offset so a filter or
 * page change reissues the request immediately.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  LoginAttemptsListResponse,
  LoginAttemptsQuery,
} from "@/types/api";

export const ADMIN_LOGIN_ATTEMPTS_KEY = [
  "admin",
  "security",
  "login-attempts",
] as const;

export interface UseAdminLoginAttemptsArgs {
  query: LoginAttemptsQuery;
  enabled?: boolean;
}

export function useAdminLoginAttempts(
  args: UseAdminLoginAttemptsArgs,
): UseQueryResult<LoginAttemptsListResponse> {
  const { query, enabled = true } = args;
  return useQuery({
    queryKey: [...ADMIN_LOGIN_ATTEMPTS_KEY, query],
    queryFn: async () => {
      const response = await api.get<LoginAttemptsListResponse>(
        "/admin/security/login-attempts",
        { params: stripEmpty(query) },
      );
      return response.data;
    },
    enabled,
  });
}

function stripEmpty(q: LoginAttemptsQuery): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}
